import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import '../../Styles/katex.css';
import '../../Styles/TextRenderer.css';
import CodeDisplay from '../../components/Partials/CodeDisplay';
import { ResourcePreview } from '../../components/Layout/ResourcePreview';
import { QuizItem } from '../../components/Layout/Items/quizItem';
import { Resource, ResourceType } from '../../types/resource';
import { parseArtifact } from './utils';
import { ImageGeneration } from '../../components/Message/ImageGeneration';
import { imageToolCall } from '../../types/message';
import { ResearchActivityDisplay } from '../../components/Message/ResearchActivityDisplay';
import InlineCodeBlock from './Components/InlineCodeBlock';
import { ImageDisplay } from './Components/ImageDisplay';
import { useWindow } from '../../contexts/WindowContext';
import { ComposeBox, type ComposeOption, type ComposeBoxMode } from '../../components/Message/ComposeBox';
import { ASK_USER_OPEN_TAG } from '../../helpers/askUser';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { parseResearchActivityRun, pendingQuestion } from './researchActivity';
import { unescapeXmlText } from '../../helpers/contentBlocks';
import type { ResearchActivityItem } from './researchActivity';
import { preprocessLatex } from './latex';
import { streamingTextPlugin } from './streamingMarkdown';

function removeLeadingIndentationForNonCode(content: string): string {
  const lines = content.split('\n');
  let inCodeFence = false;
  let sharedIndent: number | null = null;

  for (const line of lines) {
    const trimmedLine = line.trim();

    if (/^```/.test(trimmedLine)) {
      inCodeFence = !inCodeFence;
      continue;
    }

    if (inCodeFence || trimmedLine.length === 0) {
      continue;
    }

    const leadingWhitespaceMatch = line.match(/^[\t ]*/);
    const leadingWhitespace = leadingWhitespaceMatch ? leadingWhitespaceMatch[0] : '';
    const indentWidth = leadingWhitespace.replace(/\t/g, '  ').length;

    sharedIndent = sharedIndent === null ? indentWidth : Math.min(sharedIndent, indentWidth);
  }

  if (!sharedIndent) {
    return content;
  }

  const baseIndent = sharedIndent;
  inCodeFence = false;

  const processed = lines.map((line: string) => {
    const trimmedLine = line.trim();

    if (/^```/.test(trimmedLine)) {
      inCodeFence = !inCodeFence;
      return line;
    }

    if (inCodeFence || trimmedLine.length === 0) {
      return line;
    }

    let remainingIndent = baseIndent;
    let index = 0;

    while (remainingIndent > 0 && index < line.length) {
      if (line[index] === ' ') {
        remainingIndent -= 1;
        index += 1;
        continue;
      }

      if (line[index] === '\t') {
        remainingIndent -= 2;
        index += 1;
        continue;
      }

      break;
    }

    return line.slice(index);
  });

  return processed.join('\n');
}

// Insert function to normalize code fences onto their own lines
const normaliseFences = (md: string): string => {
  // put a newline BEFORE any ``` that isn't already at beginning of line
  md = md.replace(/([^\n])```/g, '$1\n```');
  return md;
};

const preprocessCitations = (markdown: string): string => {
  // Match code before citation tags so examples keep their literal markup.
  const codeOrCitation = /```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$)|`[^`\n]*(?:`|$)|<citation>([\s\S]*?)(?:<\/citation>|$)/g;
  return markdown.replace(codeOrCitation, (match, payload: string | undefined) => {
    if (!match.startsWith('<citation>')) return match;

    const urls = parseCitationUrls(payload || '');
    if (urls.length === 0) return '';
    return `<span data-citation="${encodeURIComponent(JSON.stringify(urls))}"></span>`;
  });
};

interface MarkdownRendererProps {
  content: string;
  allowedElements?: string[];
  disallowedElements?: string[];
  inline?: boolean;
  components?: Record<string, React.ComponentType<any>>;
  isStreaming?: boolean;
}

interface CodeProps {
  node?: any;
  inline?: boolean;
  className?: string;
  children: React.ReactNode;
}

const shallowArrayEqual = (a?: string[], b?: string[]): boolean => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
};

const parseCitationUrls = (value: string): string[] => {
  try {
    const candidates = JSON.parse(value);
    if (!Array.isArray(candidates)) return [];

    const seen = new Set<string>();
    return candidates.reduce<string[]>((urls, candidate) => {
      if (typeof candidate !== 'string') return urls;

      const trimmed = candidate.trim();
      try {
        const parsed = new URL(trimmed);
        const isWebUrl = parsed.protocol === 'https:' || parsed.protocol === 'http:';
        if (!isWebUrl || !parsed.hostname || parsed.username || parsed.password || seen.has(trimmed)) {
          return urls;
        }
        seen.add(trimmed);
        urls.push(trimmed);
      } catch {
        // Ignore malformed entries without breaking the surrounding Markdown.
      }
      return urls;
    }, []);
  } catch {
    return [];
  }
};

const citationText = (children: React.ReactNode): string => (
  React.Children.toArray(children)
    .map((child) => typeof child === 'string' || typeof child === 'number' ? String(child) : '')
    .join('')
);

const citationHost = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const citationCompany = (url: string): string => {
  const host = citationHost(url);
  const parts = host.split('.');
  const usesCountrySuffix = parts.length > 2 && ['co', 'com', 'org', 'net'].includes(parts[parts.length - 2]);
  const rawName = parts[Math.max(0, parts.length - (usesCountrySuffix ? 3 : 2))] || host;
  const knownNames: Record<string, string> = {
    aljazeera: 'Al Jazeera',
    bbc: 'BBC',
    cnn: 'CNN',
    nbc: 'NBC',
    cbc: 'CBC',
    nytimes: 'The New York Times',
  };
  if (knownNames[rawName.toLowerCase()]) return knownNames[rawName.toLowerCase()];
  if (rawName.length <= 3) return rawName.toUpperCase();
  return rawName
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const citationFaviconUrl = (url: string): string => (
  `https://www.google.com/s2/favicons?domain=${encodeURIComponent(citationHost(url))}&sz=64`
);

const CitationFavicon = ({ url, className }: { url: string; className: string }) => {
  const [failed, setFailed] = useState(false);
  const company = citationCompany(url);
  return (
    <span className={className} title={company}>
      {failed ? (
        <span className="citation-favicon-fallback">{company.charAt(0)}</span>
      ) : (
        <img src={citationFaviconUrl(url)} alt="" onError={() => setFailed(true)} />
      )}
    </span>
  );
};

const CitationPill = ({ children, payload }: { children?: React.ReactNode; payload?: string }) => {
  const { openNewWindowModal } = useWindow();
  const urls = useMemo(() => {
    if (payload) {
      try {
        return parseCitationUrls(decodeURIComponent(payload));
      } catch {
        return [];
      }
    }
    return parseCitationUrls(citationText(children));
  }, [children, payload]);
  const [isOpen, setIsOpen] = useState(false);

  if (urls.length === 0) return null;

  const label = urls.length === 1 ? citationCompany(urls[0]).toUpperCase() : '';
  const accessibilityLabel = urls.length === 1
    ? `View source from ${label}`
    : `View ${urls.length} sources`;
  return (
    <span className="citation">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`citation-pill${urls.length > 1 ? ' citation-pill-icons-only' : ''}`}
            aria-label={accessibilityLabel}
          >
            <span className="citation-favicons" aria-hidden="true">
              {urls.slice(0, 3).map((url) => (
                <CitationFavicon url={url} className="citation-favicon" key={url} />
              ))}
            </span>
            {label && <span>{label}</span>}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={6}
          className="citation-popover"
          aria-label="Citation sources"
        >
          {urls.map((url) => (
            <DropdownMenuItem
              className="citation-source"
              key={url}
              onSelect={() => openNewWindowModal(url)}
            >
              <CitationFavicon url={url} className="citation-source-favicon" />
              <span className="citation-source-host">{citationHost(url)}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
};

export const MarkdownRenderer = React.memo<MarkdownRendererProps>(({ content, allowedElements, disallowedElements, inline, components: propComponents = {}, isStreaming = false }) => {
  const { openNewWindowModal } = useWindow();
  const processedContent = useMemo(() => {
    let newContent = content;
    newContent = normaliseFences(newContent);
    newContent = preprocessCitations(newContent);
    newContent = preprocessLatex(newContent);
    newContent = removeLeadingIndentationForNonCode(newContent);
    return newContent;
  }, [content]);

  const components = useMemo(() => {
    const c: Record<string, React.ComponentType<any>> = {
      ...(disallowedElements || []).reduce((acc, el) => ({
        ...acc,
        [el]: ({ node, ...props }: { node: any; [key: string]: any }) => <span {...props} />
      }), {}),
      ...(allowedElements || []).reduce((acc, el) => ({
        ...acc,
        [el]: (props: any) => React.createElement(el, props)
      }), {}),
      code: ({ node, inline: isInline, className, children, ...props }: CodeProps) => {
        return (
          <InlineCodeBlock>{children}</InlineCodeBlock>
        );
      },
      // Add custom link component to open links in new window
      a: ({ node, href, children, ...props }: { node: any; href?: string; children: React.ReactNode; [key: string]: any }) => {
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (href) {
                e.preventDefault();
                openNewWindowModal(href);
              }
            }}
            {...props}
          >
            {children}
          </a>
        );
      },
      // Add custom image component with loading, error handling, and zoom
      img: ({ node, src, alt, title, ...props }: { node: any; src?: string; alt?: string; title?: string; [key: string]: any }) => {
        if (!src) return null;
        return (
          <ImageDisplay
            src={src}
            alt={alt}
            title={title}
          />
        );
      },
      citation: CitationPill,
      ...propComponents,
      span: ({ node, children, ...props }: { node: any; children: React.ReactNode; [key: string]: any }) => {
        const payload = props['data-citation'];
        if (typeof payload === 'string') return <CitationPill payload={payload} />;

        const PropSpan = propComponents.span;
        return PropSpan
          ? <PropSpan {...props}>{children}</PropSpan>
          : <span {...props}>{children}</span>;
      }
    };

    if (inline) {
      c.p = ({ node, ...props }: { node: any; [key: string]: any }) => <React.Fragment {...props} />;
      c.li = ({ node, ...props }: { node: any; [key: string]: any }) => <React.Fragment {...props} />;
    }

    return c;
  }, [allowedElements, disallowedElements, inline, propComponents, openNewWindowModal]);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: true }]]}
      rehypePlugins={[
        rehypeRaw,
        [rehypeKatex, { output: 'htmlAndMathml' }],
        ...(isStreaming ? [streamingTextPlugin] : []),
      ]}
      components={components}
    >
      {processedContent}
    </ReactMarkdown>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.content === nextProps.content &&
    shallowArrayEqual(prevProps.allowedElements, nextProps.allowedElements) &&
    shallowArrayEqual(prevProps.disallowedElements, nextProps.disallowedElements) &&
    prevProps.inline === nextProps.inline &&
    prevProps.isStreaming === nextProps.isStreaming &&
    prevProps.components === nextProps.components
  );
});

interface ParsedPart {
  type: 'text' | 'inline-code' | 'strong';
  content: string;
}

interface ParsedContentItem {
  type?: string;
  content?: string | ParsedPart[];
  items?: Array<any>;
  isOrdered?: boolean;
  start?: number | null;
  orderedMarkerType?: 'decimal' | 'lower-alpha' | 'upper-alpha' | 'lower-roman' | 'upper-roman' | null;
  level?: number;
  language?: string;
  title?: string | null;
  version?: string | null;
  previousVersion?: string | null;
  headers?: string[];
  rows?: string[][];
  url?: string;
  text?: string;
  isGenerating?: boolean;
}

interface ListContinuationBlock {
  type: 'paragraph' | 'code-block' | 'math-block';
  content: string;
  language?: string;
  isGenerating?: boolean;
}

interface ParsedListItem {
  content: Array<ParsedPart>;
  subitems: Array<any>;
  paragraphs: Array<string>;
  mathBlocks: Array<string>;
  blocks: Array<ListContinuationBlock>;
  number: number | null;
  level: number;
  indentWidth: number;
  orderedMarkerType: 'decimal' | 'lower-alpha' | 'upper-alpha' | 'lower-roman' | 'upper-roman' | null;
  isOrdered: boolean;
  checked: boolean | null;
}

const getOlTypeAttribute = (
  markerType?: 'decimal' | 'lower-alpha' | 'upper-alpha' | 'lower-roman' | 'upper-roman' | null
): 'a' | 'A' | 'i' | 'I' | undefined => {
  switch (markerType) {
    case 'lower-alpha':
      return 'a';
    case 'upper-alpha':
      return 'A';
    case 'lower-roman':
      return 'i';
    case 'upper-roman':
      return 'I';
    default:
      return undefined;
  }
};

const parseResponse = (text: string, isGenerating: boolean = false) => {
  const getOrderedMarkerType = (
    marker: string
  ): 'decimal' | 'lower-alpha' | 'upper-alpha' | 'lower-roman' | 'upper-roman' => {
    if (/^\d+$/.test(marker)) {
      return 'decimal';
    }

    if (/^[ivxlcdm]+$/.test(marker) && (marker.length > 1 || marker === 'i')) {
      return 'lower-roman';
    }

    if (/^[IVXLCDM]+$/.test(marker) && (marker.length > 1 || marker === 'I')) {
      return 'upper-roman';
    }

    if (/^[a-z]$/.test(marker)) {
      return 'lower-alpha';
    }

    return 'upper-alpha';
  };

  const romanToNumber = (roman: string): number => {
    const romanValues: Record<string, number> = {
      I: 1,
      V: 5,
      X: 10,
      L: 50,
      C: 100,
      D: 500,
      M: 1000,
    };

    let total = 0;
    let previousValue = 0;

    for (let index = roman.length - 1; index >= 0; index -= 1) {
      const currentValue = romanValues[roman[index].toUpperCase()] || 0;
      if (currentValue < previousValue) {
        total -= currentValue;
      } else {
        total += currentValue;
        previousValue = currentValue;
      }
    }

    return total;
  };

  const getOrderedListStart = (
    marker: string,
    markerType: 'decimal' | 'lower-alpha' | 'upper-alpha' | 'lower-roman' | 'upper-roman'
  ): number => {
    if (markerType === 'decimal') {
      return parseInt(marker, 10);
    }

    if (markerType === 'lower-alpha' || markerType === 'upper-alpha') {
      return marker.toLowerCase().charCodeAt(0) - 96;
    }

    return romanToNumber(marker);
  };

  // Pre-process the text to extract <think> tags
  const segments: Array<{ type: 'text'|'think'; content: string; isUnclosed?: boolean }> = [];
  let lastIndex = 0;
  
  // Regex to match both closed <think> tags and potentially unclosed ones at the end
  const thinkRegex = /<think>([\s\S]*?)(?:<\/think>|$)/g;
  let match: RegExpExecArray|null;
  
  while ((match = thinkRegex.exec(text)) !== null) {
    // Check if this think tag is quoted (literal)
    const thinkStart = match.index;
    const prevChar = thinkStart > 0 ? text[thinkStart - 1] : null;
    const nextChar = thinkStart + '<think>'.length < text.length ? text[thinkStart + '<think>'.length] : null;
    const isLiteralTag = prevChar === '"' || prevChar === '\'' || prevChar === '`' || nextChar === '"' || nextChar === '\'' || nextChar === '`';
    
    if (isLiteralTag) {
      // Skip this match and continue searching
      continue;
    }

    // Add any text before this match as a regular text segment
    if (match.index > lastIndex) {
      segments.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    
    // Determine if this is a closed or unclosed think tag
    const isUnclosed = !text.substring(match.index).includes('</think>');
    
    // Add the think content
    segments.push({ 
      type: 'think', 
      content: match[1],
      isUnclosed
    });
    
    // Update lastIndex to continue after this match
    lastIndex = match.index + match[0].length;
    
    // If we've matched an unclosed tag at the end, break the loop
    if (isUnclosed) {
      break;
    }
  }
  
  // Add any remaining text after the last match
  if (lastIndex < text.length) {
    segments.push({ type: 'text', content: text.slice(lastIndex) });
  }
  
  const parsedContent: Array<ParsedContentItem> = [];
  
  // Process each segment
  for (const segment of segments) {
    if (segment.type === 'think') {
      // For think segments, directly add them to the parsed content
      parsedContent.push({
        type: 'think',
        content: segment.content,
        isGenerating: segment.isUnclosed && isGenerating
      });
    } else {
      // For text segments, process them using the existing line-by-line logic
      const lines = segment.content.split('\n');
      
      // Continue with the existing parseResponse logic for text segments
      let currentList: {
        items: Array<any>;
        isOrdered: boolean;
        start: number | null;
        orderedMarkerType: 'decimal' | 'lower-alpha' | 'upper-alpha' | 'lower-roman' | 'upper-roman' | null;
        level: number;
      } | null = null;
      let currentListType: string | null = null;
      let currentListStack: Array<ParsedListItem> = [];
      let currentListIndentWidths: number[] = [];
      let currentListItem: ParsedListItem | null = null;
      let inCodeBlock = false;
      let codeBlockLanguage = '';
      let codeBlockContent = '';
      const codeBlockTarget: { current: ParsedListItem | null } = { current: null };
      let inTable = false;
      let tableHeaders: Array<string> = [];
      let tableRows: Array<Array<string>> = [];
      let inMathBlock = false;
      let mathBlockContent = '';
      let processingListItemMath = false;
      let currentListSawBlankLine = false;

      const normalizeListLevel = (indentWidth: number): number => {
        if (!currentList || currentListIndentWidths.length === 0) {
          currentListIndentWidths = [indentWidth];
          return 0;
        }

        while (
          currentListIndentWidths.length > 1 &&
          indentWidth < currentListIndentWidths[currentListIndentWidths.length - 1]
        ) {
          currentListIndentWidths.pop();
        }

        const existingLevel = currentListIndentWidths.indexOf(indentWidth);
        if (existingLevel !== -1) {
          currentListIndentWidths = currentListIndentWidths.slice(0, existingLevel + 1);
          return existingLevel;
        }

        const currentDeepestIndent = currentListIndentWidths[currentListIndentWidths.length - 1];
        if (indentWidth > currentDeepestIndent) {
          currentListIndentWidths.push(indentWidth);
          return currentListIndentWidths.length - 1;
        }

        currentListIndentWidths.push(indentWidth);
        return currentListIndentWidths.length - 1;
      };

      const processListItem = (line: string, listType: string) => {
        const indentMatch = line.match(/^\s*/)![0];
        const indentWidth = indentMatch.replace(/\t/g, '  ').length;
        const trimmedLine = line.trim();
        let content = '';
        let number = null;
        let orderedMarkerType: 'decimal' | 'lower-alpha' | 'upper-alpha' | 'lower-roman' | 'upper-roman' | null = null;
        let matched = false;
        let checked: boolean | null = null;
    
        if (listType === 'checkbox') {
          const match = trimmedLine.match(/^(?:[•\-*+]\s+)?\[([ xX])\](?:\s+(.*))?$/);
          if (match) {
            matched = true;
            checked = match[1].toLowerCase() === 'x';
            content = match[2] || '';
          }
        } else if (listType === 'ordered') {
          const match = trimmedLine.match(/^([0-9]+|[a-zA-Z]|[ivxlcdm]+|[IVXLCDM]+)\.(?:\s+(.*))?$/);
          if (match) {
            matched = true;
            orderedMarkerType = getOrderedMarkerType(match[1]);
            number = getOrderedListStart(match[1], orderedMarkerType);
            content = match[2] || '';
          }
        } else {
          const match = trimmedLine.match(/^([•\-*+])(?:\s+(.*))?$/);
          if (match) {
            matched = true;
            content = match[2] || '';
          }
        }
    
        if (!matched) return;

        currentListSawBlankLine = false;
        const level = normalizeListLevel(indentWidth);
        const isItemOrdered = listType === 'ordered';
        const isCheckbox = listType === 'checkbox';
        const rootListType = isCheckbox ? 'checkbox' : isItemOrdered ? `ordered:${orderedMarkerType}` : 'unordered';
    
        const processedParts: ParsedPart[] = [{ type: 'text', content }];
    
        const newItem = {
          content: processedParts,
          subitems: [],
          paragraphs: [],
          mathBlocks: [],
          blocks: [],
          number,
          level,
          indentWidth,
          orderedMarkerType,
          isOrdered: isItemOrdered,
          checked
        };
    
        const parentItem = level > 0 ? currentListStack[level - 1] || null : null;

        if (level > 0 && parentItem) {
          parentItem.subitems.push(newItem);
        } else if (currentList && (level > 0 || currentListType === rootListType)) {
          currentList.items.push(newItem);
        } else {
          finalizePreviousList();
          currentList = {
            items: [newItem],
            isOrdered: isItemOrdered,
            start: isItemOrdered ? number : null,
            orderedMarkerType,
            level
          };
          parsedContent.push(currentList);
          currentListType = rootListType;
        }

        currentListStack = currentListStack.slice(0, level);
        currentListStack[level] = newItem;
        currentListItem = newItem;
      };
    
      const finalizePreviousList = () => {
        if (currentList) {
          // Remove empty lists
          if (currentList.items.length === 0) {
            parsedContent.pop();
          }
          currentList = null;
          currentListItem = null;
          currentListType = null;
          currentListStack = [];
          currentListIndentWidths = [];
          processingListItemMath = false;
          currentListSawBlankLine = false;
        }
      };

      const processLine = (line: string, lineIndex: number) => {

        const trimmedLine = line.trim();

        if (trimmedLine.startsWith('```')) {
          if (inCodeBlock) {
            const codeBlock = {
              type: 'code-block',
              content: codeBlockContent, // Don't trim here
              language: codeBlockLanguage,
              title: null,
              version: null,
              previousVersion: null,
            };

            if (codeBlockTarget.current) {
              codeBlockTarget.current.blocks.push({
                type: 'code-block',
                content: codeBlockContent,
                language: codeBlockLanguage,
              });
            } else {
              // closing fence - Use the accumulated content directly
              parsedContent.push(codeBlock);
            }

            inCodeBlock = false;
            codeBlockContent = '';
            codeBlockLanguage = '';
            codeBlockTarget.current = null;
          } else {
            // opening fence
            const leadingSpaces = (line.match(/^\s*/)?.[0] || '').replace(/\t/g, '  ').length;
            const belongsToCurrentListItem = currentListItem
              && (!currentListSawBlankLine || leadingSpaces >= currentListItem.indentWidth + 2);

            inCodeBlock = true;
            codeBlockTarget.current = belongsToCurrentListItem ? currentListItem : null;
            if (!codeBlockTarget.current && currentList) {
              finalizePreviousList();
            }

            // parse language without greedy backtracking
            const langMatch = trimmedLine.match(/^```([^\s`]+)/);
            codeBlockLanguage = langMatch ? langMatch[1] : 'plaintext';
            // IMPORTANT: Initialize codeBlockContent for a new block if needed
            codeBlockContent = '';
          }
          return;
        }

        // Everything that follows should be *ignored* while inside a code fence
        if (inCodeBlock) {
          // Preserve the source's real line boundary while the final line streams.
          // Do not invent a newline for the in-flight final line. Keeping that
          // line stable lets the code renderer update it without remounting
          // every completed line above it.
          codeBlockContent += line + (lineIndex < lines.length - 1 ? '\n' : '');
          return;
        }


        const checkboxMatch = line.match(/^\s*(?:[•\-*+]\s+)?\[([ xX])\](?:\s+(.*))?$/);
        const orderedListMatch   = line.match(/^\s*([0-9]+|[a-zA-Z]|[ivxlcdm]+|[IVXLCDM]+)\.(?:\s+(.*))?$/);
        const unorderedListMatch = line.match(/^\s*([•\-*+])(?:\s+(.*))?$/);

        if (checkboxMatch) {
          processListItem(line, 'checkbox');
          return;
        }

        if (orderedListMatch) {
          processListItem(line, 'ordered');
          return;
        }
    
        if (unorderedListMatch) {
          processListItem(line, 'unordered');
          return;
        }

        const quoteMatch = trimmedLine.match(/<quote>([\s\S]*?)<\/quote>/);
        if (quoteMatch) {
          finalizePreviousList();
          parsedContent.push({
            type: 'quote',
            content: quoteMatch[1].trim()
          });
          return;
        }
    
        // Check if we're in a math block
        if (inMathBlock) {
          if (trimmedLine === '\\]' || trimmedLine === '$$') {
            // Math block is ending
            if (currentListItem) {
              // Add to current list item's math blocks if we're in a list
              currentListItem.mathBlocks.push(mathBlockContent.trim());
              currentListItem.blocks.push({ type: 'math-block', content: mathBlockContent.trim() });
              processingListItemMath = false;
            } else {
              // Otherwise add as a standalone math block
              parsedContent.push({ type: 'math-block', content: mathBlockContent.trim() });
            }
            inMathBlock = false;
            mathBlockContent = '';
          } else {
            mathBlockContent += line + '\n';
          }
          return;
        }
    
        // Check for math block start
        if (trimmedLine === '\\[' || trimmedLine === '$$') {
          inMathBlock = true;
          // If we're inside a list item, mark that we're processing a math block for this list item
          if (currentListItem) {
            processingListItemMath = true;
          }
          return;
        }
  
        // If the line isn't part of a list math block, handle other content types
        if (!processingListItemMath) {
          // Blank lines inside a list block are allowed without finalizing
          if (currentList && trimmedLine.length === 0) {
            currentListSawBlankLine = true;
            return;
          }

          // Add indented paragraphs to list items if we're in a list context
          if (currentListItem && trimmedLine.length > 0) {
            // Check if this is an indented continuation of the list item
            const leadingSpaces = (line.match(/^\s*/)?.[0] || '').replace(/\t/g, '  ').length;
            if (leadingSpaces >= currentListItem.indentWidth + 2) {
              // This is an indented paragraph belonging to the list item
              currentListItem.paragraphs.push(trimmedLine);
              currentListItem.blocks.push({ type: 'paragraph', content: trimmedLine });
              currentListSawBlankLine = false;
              return;
            }

            const startsNewBlock = /^#{1,6}\s+/.test(trimmedLine)
              || /^> /.test(trimmedLine)
              || trimmedLine === '---'
              || trimmedLine.startsWith('$$')
              || trimmedLine === '\\['
              || trimmedLine.includes('|');

            if (!currentListSawBlankLine && !startsNewBlock) {
              currentListItem.paragraphs.push(trimmedLine);
              currentListItem.blocks.push({ type: 'paragraph', content: trimmedLine });
              return;
            }
          }
    
          // If not a list indent continuation, handle other content types
          finalizePreviousList();
    
          // Handle headers using Markdown syntax
          const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.*)/);
          if (headerMatch) {
            const level = headerMatch[1].length;
            const headerContent = headerMatch[2].trim();
            parsedContent.push({ type: 'heading', level, content: headerContent });
            return; // Prevent falling through to other types
          }
    
          // Handle tables - support both bordered (| col |) and non-bordered (col | col) GFM formats
          const isSeparatorLine = trimmedLine.replace(/[\s|:-]/g, '').length === 0
            && trimmedLine.includes('|')
            && trimmedLine.includes('-');
          const isBorderedTableRow = trimmedLine.startsWith('|') && trimmedLine.endsWith('|') && !isSeparatorLine;
          const isNonBorderedTableRow = !isBorderedTableRow && trimmedLine.includes('|') && inTable && !isSeparatorLine;

          if (isBorderedTableRow || isNonBorderedTableRow) {
            if (!inTable) {
              inTable = true;
              tableHeaders = trimmedLine.split('|').slice(1, -1).map((header: string) => header.trim());
            } else if (isBorderedTableRow) {
              tableRows.push(trimmedLine.split('|').slice(1, -1).map((cell: string) => cell.trim()));
            } else {
              tableRows.push(trimmedLine.split('|').map((cell: string) => cell.trim()));
            }
            return;
          } else if (isSeparatorLine) {
            if (!inTable) {
              // Non-bordered table: retroactively convert the previous paragraph into the table header
              const lastItem = parsedContent[parsedContent.length - 1];
              if (lastItem?.type === 'paragraph' && typeof lastItem.content === 'string' && lastItem.content.includes('|')) {
                parsedContent.pop();
                inTable = true;
                tableHeaders = lastItem.content.split('|').map((h: string) => h.trim()).filter(Boolean);
              }
            }
            return;
          } else if (inTable) {
            // End of table
            parsedContent.push({
              type: 'table',
              content: '',
              items: [],
              headers: tableHeaders,
              rows: tableRows
            });
            inTable = false;
            tableHeaders = [];
            tableRows = [];
          }
    
          // Handle blockquotes
          if (trimmedLine.startsWith('> ')) {
            finalizePreviousList();
            parsedContent.push({ type: 'blockquote', content: trimmedLine.slice(2) });
            return;
          }
    
          // Handle horizontal rules
          if (trimmedLine === '---') {
            finalizePreviousList();
            parsedContent.push({ type: 'horizontal-rule', content: '' });
            return;
          }
    
          // Add handling for display math blocks that start and end with $$ on a single line
          else if (trimmedLine.startsWith('$$') && trimmedLine.endsWith('$$')) {
            if (currentListItem) {
              // If we're in a list, add math block to the current list item
              currentListItem.mathBlocks.push(trimmedLine.slice(2, -2).trim());
              currentListItem.blocks.push({ type: 'math-block', content: trimmedLine.slice(2, -2).trim() });
            } else {
              // Otherwise add as a standalone math block
              finalizePreviousList(); // Finalize any list before adding block content
              parsedContent.push({
                type: 'math-block',
                content: trimmedLine.slice(2, -2) // Remove $$ from start and end
              });
            }
            return; // Return after handling math block to prevent double processing
          }
    
          // Fallback: If no other type matched, treat as a paragraph
          // Ensure it's not an empty line and not part of a multi-line structure handled elsewhere
          else if (trimmedLine.length > 0 && !inCodeBlock && !inTable && !inMathBlock) {
            finalizePreviousList();
            parsedContent.push({ type: 'paragraph', content: trimmedLine });
          }
        }
      };

      lines.forEach((line: string, lineIndex: number) => processLine(line, lineIndex));

      // ADDED: Final check for unclosed code block after processing all lines
      if (inCodeBlock) {
        const targetListItem = codeBlockTarget.current;
        if (targetListItem) {
          targetListItem.blocks.push({
            type: 'code-block',
            content: codeBlockContent,
            language: codeBlockLanguage,
            isGenerating,
          });
        } else {
          parsedContent.push({
            type: 'code-block',
            content: codeBlockContent, // Use accumulated content directly
            language: codeBlockLanguage,
            title: null,
            version: null,
            previousVersion: null,
            isGenerating,
          });
        }
        // Reset flags just in case, though loop is ending
        inCodeBlock = false; 
        codeBlockContent = '';
        codeBlockLanguage = '';
        codeBlockTarget.current = null;
      }

      if (inTable) {
        parsedContent.push({
          type: 'table',
          content: '',
          items: [],
          headers: tableHeaders,
          rows: tableRows
        });
        inTable = false;
        tableHeaders = [];
        tableRows = [];
      }

      if (inMathBlock) {
        parsedContent.push({ type: 'math-block', content: mathBlockContent.trim() });
        inMathBlock = false;
        mathBlockContent = '';
      }

      finalizePreviousList();
    }
  }
  
  if (isGenerating && parsedContent.length > 0) {
    const lastItem = parsedContent[parsedContent.length - 1];
    if (lastItem.type !== 'code-block' && lastItem.type !== 'think') {
      lastItem.isGenerating = true;
    }
  }

  return parsedContent;
};

const groupSubitemsByType = (subitems: any[]): Array<{ isOrdered: boolean; items: any[]; orderedMarkerType?: string | null }> => {
  const groups: Array<{ isOrdered: boolean; items: any[]; orderedMarkerType?: string | null }> = [];
  for (const subitem of subitems) {
    const isOrdered = !!subitem.isOrdered;
    const last = groups[groups.length - 1];
    if (last && last.isOrdered === isOrdered && last.orderedMarkerType === (subitem.orderedMarkerType || null)) {
      last.items.push(subitem);
    } else {
      groups.push({
        isOrdered,
        items: [subitem],
        orderedMarkerType: subitem.orderedMarkerType || null
      });
    }
  }
  return groups;
};

const renderListItem = (item: any, index: number, isOrdered: boolean, level: number = 0, isStreaming: boolean = false) => {
  // Calculate effective level based on parent level
  const effectiveLevel = Math.min(item.level || 0, 3); // Cap at level 3 for reasonable indentation

  const markerType = item.orderedMarkerType || 'decimal';
  const listStyleType = isOrdered
    ? markerType === 'lower-alpha' ? 'lower-alpha'
      : markerType === 'upper-alpha' ? 'upper-alpha'
      : markerType === 'lower-roman' ? 'lower-roman'
      : markerType === 'upper-roman' ? 'upper-roman'
      : 'decimal'
    : 'disc';

  const isCheckbox = item.checked !== null && item.checked !== undefined;
  const continuationBlocks: ListContinuationBlock[] = Array.isArray(item.blocks) ? item.blocks : [];
  const hasContinuationBlocks = continuationBlocks.length > 0;
  const hasSubitems = item.subitems && item.subitems.length > 0;
  const subitemGroups = hasSubitems ? groupSubitemsByType(item.subitems) : [];
  const streamsPrimaryContent = isStreaming
    && !hasSubitems
    && !hasContinuationBlocks
    && !(item.paragraphs && item.paragraphs.length > 0);

  return (
    <li
      key={index}
      className={`ai-response-list-item level-${effectiveLevel}${isCheckbox ? ' ai-response-checkbox-item' : ''}`}
      value={isOrdered && markerType === 'decimal' ? item.number : undefined}
      style={{ listStyleType: isCheckbox ? 'none' : listStyleType }}
    >
      <div className="ai-response-list-item-content">
        {isCheckbox && (
          <span className={`ai-response-checkbox ${item.checked ? 'checked' : ''}`}>
            {item.checked ? '✓' : ''}
          </span>
        )}
        {item.content.map((part: ParsedPart, partIndex: number) => {
          if (part.type === 'inline-code') {
            return (
              <InlineCodeBlock key={partIndex}>{part.content}</InlineCodeBlock>
            );
          }
          if (part.type === 'text') {
            return (
              <MarkdownRenderer
                key={partIndex}
                content={part.content}
                inline={true}
                isStreaming={streamsPrimaryContent && partIndex === item.content.length - 1}
              />
            );
          }
          return null;
        })}
      </div>

      {hasContinuationBlocks && (
        <div className="ai-response-list-item-blocks">
          {continuationBlocks.map((block, blockIndex) => {
            if (block.type === 'paragraph') {
              return (
                <div key={blockIndex} className="ai-response-list-paragraph">
                  <MarkdownRenderer
                    content={block.content}
                    inline={false}
                    isStreaming={isStreaming && !hasSubitems && blockIndex === continuationBlocks.length - 1}
                  />
                </div>
              );
            }

            if (block.type === 'code-block') {
              return (
                <CodeDisplay
                  key={blockIndex}
                  language={block.language || ''}
                  showHeader={true}
                  content={block.content}
                  isStreaming={block.isGenerating || false}
                />
              );
            }

            if (block.type === 'math-block') {
              return (
                <MarkdownRenderer
                  key={blockIndex}
                  content={`$$\n${block.content}\n$$`}
                  inline={false}
                />
              );
            }

            return null;
          })}
        </div>
      )}

      {!hasContinuationBlocks && item.paragraphs && item.paragraphs.length > 0 && (
        <div className="ai-response-list-item-paragraphs">
          {item.paragraphs.map((para: string, paraIndex: number) => (
            <div key={paraIndex} className="ai-response-list-paragraph">
              <MarkdownRenderer
                content={para}
                inline={false}
                isStreaming={isStreaming && !hasSubitems && paraIndex === item.paragraphs.length - 1}
              />
            </div>
          ))}
        </div>
      )}

      {/* Render math blocks inside the list item */}
      {!hasContinuationBlocks && item.mathBlocks && item.mathBlocks.length > 0 && (
        <div className="ai-response-list-item-math-blocks">
          {item.mathBlocks.map((mathContent: string, mathIndex: number) => (
            <MarkdownRenderer
              key={mathIndex}
              content={`$$\n${mathContent}\n$$`}
              inline={false}
            />
          ))}
        </div>
      )}

      {hasSubitems && (
        subitemGroups.map((group, groupIndex) =>
          group.isOrdered ? (
            <ol
              key={groupIndex}
              className={`ai-response-sublist level-${effectiveLevel}`}
              start={group.items[0]?.number || undefined}
              type={getOlTypeAttribute(group.orderedMarkerType as any)}
            >
              {group.items.map((subitem: any, subIndex: number) =>
                renderListItem(
                  subitem,
                  subIndex,
                  true,
                  effectiveLevel + 1,
                  isStreaming
                    && groupIndex === subitemGroups.length - 1
                    && subIndex === group.items.length - 1
                )
              )}
            </ol>
          ) : (
            <ul key={groupIndex} className={`ai-response-sublist level-${effectiveLevel}`}>
              {group.items.map((subitem: any, subIndex: number) =>
                renderListItem(
                  subitem,
                  subIndex,
                  false,
                  effectiveLevel + 1,
                  isStreaming
                    && groupIndex === subitemGroups.length - 1
                    && subIndex === group.items.length - 1
                )
              )}
            </ul>
          )
        )
      )}
    </li>
  );
};

interface ParsedContentProps {
  parsedContent: ParsedContentItem[];
}

// Create a new component for the parsed content
export const ParsedContent = React.memo(({ parsedContent }: ParsedContentProps) => {
  // Updated renderMarkdownContent function to handle content arrays
  const renderMarkdownContent = (content: string | Array<{ type: string, content: string }>, isStreaming = false) => {
    if (Array.isArray(content)) {
      return content.map((part, partIndex) => {
        if (part.type === 'strong') {
          return <strong key={partIndex}>{part.content}</strong>;
        }
        if (part.type === 'inline-code') {
          return <InlineCodeBlock key={partIndex}>{part.content}</InlineCodeBlock>;
        }
        if (part.type === 'text') {
          return <span key={partIndex}>{part.content}</span>;
        }
        return null;
      });
    } else {
      return <MarkdownRenderer content={content} inline={true} isStreaming={isStreaming} components={{ p: ({ children, ...props }) => <span {...props}>{children}</span> }} />;
    }
  };

  return (
    <>
      {parsedContent.map((item, index) => {
        if (item.isOrdered !== undefined && item.items) {
          return item.isOrdered ? (
            <ol
              key={index}
              className="ai-response-list ai-response-ordered-list"
              start={item.start || undefined}
              type={getOlTypeAttribute(item.orderedMarkerType)}
            >
              {item.items.map((listItem, listIndex) =>
                renderListItem(listItem, listIndex, true, 1, Boolean(item.isGenerating) && listIndex === item.items!.length - 1)
              )}
            </ol>
          ) : (
            <ul key={index} className="ai-response-list">
              {item.items.map((listItem, listIndex) =>
                renderListItem(listItem, listIndex, false, 1, Boolean(item.isGenerating) && listIndex === item.items!.length - 1)
              )}
            </ul>
          );
        }

        // Existing rendering logic for other types (heading, paragraph, etc.)
        switch (item.type) {
          case 'heading':
            return React.createElement(`h${item.level}`, {
              key: `heading-${item.level}-${index}`,
              className: `ai-response-heading ai-response-heading-${item.level}`,
              children: (
                <MarkdownRenderer
                  content={
                    Array.isArray(item.content)
                      ? item.content.map((part) => part.content).join('')
                      : item.content || ''
                  }
                  inline={true}
                  isStreaming={item.isGenerating || false}
                />
              ),
            });
          case 'paragraph':
            return (
              <div key={index} className="ai-response-paragraph">
                {/* Directly use MarkdownRenderer for paragraphs, letting it handle inline math */}
                <MarkdownRenderer
                  content={typeof item.content === 'string' ? item.content : ''} /* Ensure content is string */
                  isStreaming={item.isGenerating || false}
                  /* Do not force inline=true here, let react-markdown decide */
                  /* Do not override 'p' component here */
                />
              </div>
            );
          case 'strong':
            return (
              <strong key={index} className="ai-response-strong">
                {Array.isArray(item.content)
                  ? item.content.map((part, i) =>
                      part.type === 'inline-code'
                        ? <InlineCodeBlock key={i}>{part.content}</InlineCodeBlock>
                        : part.content
                    )
                  : item.content
                }
              </strong>
            );
          case 'blockquote':
            return (
              <blockquote key={`${index}-${item.content}`} className="ai-response-blockquote">
                {renderMarkdownContent(item.content || '', item.isGenerating || false)}
              </blockquote>
            );
          case 'horizontal-rule':
            return <hr key={index} className="ai-response-hr" />;
          case 'table':
            return (
              <div className="table-wrapper" key={index}>
                <table className="ai-response-table">
                  <thead>
                    <tr>
                      {item.headers?.map((header, headerIndex) => (
                        <th key={headerIndex}>
                          {renderMarkdownContent(
                            header,
                            Boolean(item.isGenerating)
                              && (item.rows?.length || 0) === 0
                              && headerIndex === (item.headers?.length || 0) - 1
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {item.rows?.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex}>{renderMarkdownContent(
                            cell,
                            Boolean(item.isGenerating)
                              && rowIndex === (item.rows?.length || 0) - 1
                              && cellIndex === row.length - 1
                          )}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'code-block':
            return <CodeDisplay key={index} language={item.language || ''} showHeader={true} content={item.content ? (typeof item.content === 'string' ? item.content : item.content.map((part: { content: string }) => part.content).join('')) : ''} isStreaming={item.isGenerating || false} />;
          case 'quote':
            return (
              <div key={index} className="quote-content">
                {renderMarkdownContent(item.content || '', item.isGenerating || false)}
              </div>
            );
          case 'math-block':
            return (
              <MarkdownRenderer
                key={index}
                content={`$$\n${item.content}\n$$`}
                inline={false}
              />
            );
          case 'think':
            return (
              <ResearchActivityDisplay
                key={`think-${index}`}
                items={[{
                  kind: 'thinking',
                  content: item.content as string,
                  queries: [],
                  urls: [],
                  files: [],
                  isComplete: !(item.isGenerating || false),
                }]}
                messageId=""
                isActive={item.isGenerating || false}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
});

interface AIDisplayRendererProps {
  content: string;
  isGenerating?: boolean;
}

export const AIDisplayRenderer = React.memo(({ content, isGenerating = false }: AIDisplayRendererProps) => {
  const normalizedContent = useMemo(() => normaliseFences(content), [content]);
  const parsedContent = useMemo(() => parseResponse(normalizedContent, isGenerating), [normalizedContent, isGenerating]);
  return <ParsedContent parsedContent={parsedContent} />;
});

interface AIResponseRendererProps {
  content: string;
  messageId?: string | null;
  handleResourceClick?: (resourceTitle: string, messageId: string) => void;
  isGenerating: boolean;
  resourcePreviewState?: {
    resources: Resource[];
    viewedResource: Resource | null;
    isResourceViewVisible: boolean;
  };
}

/**
 * Return the index of the *matching* </tagName> that closes the opener
 * at openIndex.  If none is found, return -1.
 */
const findMatchingClosingTag = (
  str: string,
  tagName: string,
  openIndex: number
): number => {
  const openTag  = `<${tagName}>`;
  const closeTag = `</${tagName}>`;

  let idx   = openIndex + openTag.length;
  let depth = 1;

  while (idx < str.length) {
    const nextOpen  = str.indexOf(openTag,  idx);
    const nextClose = str.indexOf(closeTag, idx);

    // no more closing tag → malformed
    if (nextClose === -1) return -1;

    // another opener appears *before* the next closer → descend
    if (nextOpen !== -1 && nextOpen < nextClose) {
      depth++;
      idx = nextOpen + openTag.length;
      continue;
    }

    // we found a closer
    depth--;
    if (depth === 0) return nextClose;      // matching close tag
    idx = nextClose + closeTag.length;      // keep looking
  }

  return -1; // none found
};

// Helper function to extract content from XML-like tags
const extractTagContent = (xmlString: string, tagName: string, from: number = 0): string | null => {
  const startTag = `<${tagName}>`;
  const open = xmlString.indexOf(startTag, from);
  if (open === -1) return null;

  const close = findMatchingClosingTag(xmlString, tagName, open);
  if (close === -1) return null;

  return xmlString.slice(open + startTag.length, close);
};

/**
 * Extract a (possibly streaming) leaf-tag value from a body — returns the
 * fully-closed value if present, otherwise everything after the opener up to
 * the next tag boundary (works for in-flight content as it streams).
 *
 * A literal `<` in prose (e.g. "a < b") is kept: we only cut at a `<` that
 * actually starts a tag (`</content>`, `<option>`, …) or a trailing partial
 * tag still streaming in (`<`, `</`, `</cont`).
 */
const extractStreamingLeaf = (body: string, tagName: string): string => {
  const closed = extractTagContent(body, tagName);
  if (closed !== null) return closed;
  const open = `<${tagName}>`;
  const idx = body.indexOf(open);
  if (idx === -1) return '';
  const after = body.slice(idx + open.length);
  for (let lt = after.indexOf('<'); lt !== -1; lt = after.indexOf('<', lt + 1)) {
    const rest = after.slice(lt);
    const isTagStart = /^<\/?[a-zA-Z]/.test(rest);
    const isPartialTail = rest === '<' || rest === '</';
    if (isTagStart || isPartialTail) {
      return after.slice(0, lt);
    }
  }
  return after;
};

/**
 * Parse the body of a <compose>...</compose> block.
 * Schema (strict): optional <to>, then <options> wrapping one or more <option>.
 * Supports streaming/unclosed bodies — returns whatever options can be
 * recovered, including a partial trailing option.
 */
const parseComposeBody = (
  inner: string
): { to: string | undefined; options: ComposeOption[] } => {
  const to = extractStreamingLeaf(inner, 'to').trim() || undefined;

  // Locate <options> body (supports unclosed during streaming).
  let optionsBody = '';
  const closedOptions = extractTagContent(inner, 'options');
  if (closedOptions !== null) {
    optionsBody = closedOptions;
  } else {
    const openIdx = inner.indexOf('<options>');
    if (openIdx !== -1) {
      optionsBody = inner.slice(openIdx + '<options>'.length);
    }
  }

  const options: ComposeOption[] = [];
  if (optionsBody) {
    // Closed <option>...</option> blocks first.
    const optionRegex = /<option>([\s\S]*?)<\/option>/g;
    let m: RegExpExecArray | null;
    let lastEnd = 0;
    while ((m = optionRegex.exec(optionsBody)) !== null) {
      const block = m[1];
      options.push(buildComposeOption(block));
      lastEnd = m.index + m[0].length;
    }
    // Trailing unclosed <option> (streaming).
    const tail = optionsBody.slice(lastEnd);
    const openOptionIdx = tail.indexOf('<option>');
    if (openOptionIdx !== -1) {
      const partialBlock = tail.slice(openOptionIdx + '<option>'.length);
      const partial = buildComposeOption(partialBlock);
      if (partial.content || partial.subject || partial.title) {
        options.push(partial);
      }
    }
  }

  return { to, options };
};

const buildComposeOption = (block: string): ComposeOption => ({
  title: extractStreamingLeaf(block, 'title').trim() || undefined,
  subject: extractStreamingLeaf(block, 'subject').trim() || undefined,
  content: extractStreamingLeaf(block, 'content').trim(),
});

/**
 * Parse the body of a legacy <mail>...</mail> block (flat <to>/<subject>/<content>
 * children) into the compose shape: a single option. Kept for backward
 * compatibility with messages stored before the <compose> schema.
 */
const parseLegacyMailBody = (
  inner: string
): { to: string | undefined; options: ComposeOption[] } => {
  const to = extractStreamingLeaf(inner, 'to').trim() || undefined;
  const subject = extractStreamingLeaf(inner, 'subject').trim() || undefined;
  const content = extractStreamingLeaf(inner, 'content').trim();
  return { to, options: [{ subject, content }] };
};

interface ShoppingRetailer { name: string; url: string; price: number; priceCurrency: string }
interface ShoppingProduct { id: string; name: string; subtitle: string; description: string; imageUrl: string; retailers: ShoppingRetailer[] }

const shoppingField = (block: string, tag: string): string => {
  const closed = extractTagContent(block, tag);
  if (closed !== undefined && closed !== null) return closed.trim();
  const start = block.toLowerCase().indexOf(`<${tag.toLowerCase()}>`);
  if (start === -1) return '';
  const rest = block.slice(start + tag.length + 2);
  const next = rest.indexOf('<');
  return (next === -1 ? rest : rest.slice(0, next)).trim();
};

const parseShoppingRetailers = (block: string): ShoppingRetailer[] => {
  const retailersBlock = shoppingField(block, 'retailers') || block.slice(Math.max(0, block.toLowerCase().indexOf('<retailers>') + 11));
  const retailers: ShoppingRetailer[] = [];
  const regex = /<retailer\b[^>]*>([\s\S]*?)(?:<\/retailer\s*>|$)/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(retailersBlock))) {
    const name = shoppingField(match[1], 'name');
    const url = shoppingField(match[1], 'url');
    const rawPrice = shoppingField(match[1], 'price');
    const price = rawPrice === '' ? Number.NaN : Number(rawPrice);
    if (name && url && Number.isFinite(price) && price >= 0) retailers.push({ name, url, price, priceCurrency: shoppingField(match[1], 'priceCurrency') || 'USD' });
    if (!/<\/retailer\s*>$/i.test(match[0])) break;
  }
  return retailers;
};

const parseShoppingProducts = (body: string): ShoppingProduct[] => {
  const products: ShoppingProduct[] = [];
  const regex = /<product\b[^>]*>([\s\S]*?)(?:<\/product\s*>|$)/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(body))) {
    const block = match[1];
    const rawId = shoppingField(block, 'id');
    const name = shoppingField(block, 'name');
    if (rawId || name) products.push({
      id: rawId || `streaming-product-${products.length}`,
      name: name || '…', subtitle: shoppingField(block, 'subtitle'),
      description: shoppingField(block, 'description'), imageUrl: shoppingField(block, 'imageUrl'),
      retailers: parseShoppingRetailers(block),
    });
    if (!/<\/product\s*>$/i.test(match[0])) break;
  }
  return products;
};

const formatShoppingPrice = (retailer: ShoppingRetailer): string => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: retailer.priceCurrency }).format(retailer.price); }
  catch { return `${retailer.price} ${retailer.priceCurrency}`; }
};

const ShoppingDisplay = ({ body }: { body: string }) => {
  const products = parseShoppingProducts(body);
  const [selected, setSelected] = React.useState<ShoppingProduct | null>(null);
  const dialogRef = React.useRef<HTMLElement>(null);
  const previouslyFocusedRef = React.useRef<HTMLElement | null>(null);

  const openProduct = React.useCallback((product: ShoppingProduct) => {
    previouslyFocusedRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    setSelected(product);
  }, []);

  const closeProduct = React.useCallback(() => setSelected(null), []);
  const selectedId = selected?.id;

  React.useEffect(() => {
    if (selected) setSelected(products.find(product => product.id === selected.id) || selected);
    // The body changes token-by-token while a product streams.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body]);

  React.useEffect(() => {
    if (!selectedId) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => {
      dialogRef.current?.querySelector<HTMLElement>('.shopping-close')?.focus();
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeProduct();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocusedRef.current?.focus();
    };
  }, [closeProduct, selectedId]);

  if (!products.length) return null;
  return <section className="shopping-list">
    {products.map(product => {
      const currencies = new Set(product.retailers.map(retailer => retailer.priceCurrency));
      const lead = currencies.size === 1
        ? product.retailers.reduce<ShoppingRetailer | undefined>((best, item) => !best || item.price < best.price ? item : best, undefined)
        : product.retailers[0];
      return <button type="button" className="shopping-row" key={product.id} onClick={() => openProduct(product)}>
        <span className="shopping-thumb">{product.imageUrl ? <img src={product.imageUrl} alt="" loading="lazy" /> : '▧'}</span>
        <span><strong>{product.name}</strong>{product.subtitle && <small>{product.subtitle}</small>}{lead && <em>{product.retailers.length > 1 && currencies.size === 1 ? 'From ' : ''}{formatShoppingPrice(lead)}</em>}</span>
        <b aria-hidden="true">›</b>
      </button>;
    })}
    {selected && <div className="shopping-overlay" role="presentation" onMouseDown={event => event.target === event.currentTarget && closeProduct()}>
      <article ref={dialogRef} className="shopping-detail" role="dialog" aria-modal="true" aria-label={selected.name}>
        <button type="button" className="shopping-close" onClick={closeProduct} aria-label="Close">×</button>
        <div className="shopping-hero">{selected.imageUrl ? <img src={selected.imageUrl} alt={selected.name} /> : '▧'}</div>
        <div className="shopping-detail-body"><h2>{selected.name}</h2>{selected.subtitle && <p>{selected.subtitle}</p>}
          {!!selected.retailers.length && <section><h3>Buy from</h3>{selected.retailers.map(retailer => <a href={retailer.url} target="_blank" rel="noopener noreferrer" key={retailer.url}><span><strong>{retailer.name}</strong><small>{formatShoppingPrice(retailer)}</small></span><b>Visit</b></a>)}</section>}
          {selected.description && <div className="shopping-about"><MarkdownRenderer content={selected.description} /></div>}
        </div>
      </article>
    </div>}
  </section>;
};

export const AIResponseRenderer = ({
  content,
  messageId = null,
  handleResourceClick,
  isGenerating = false,
  resourcePreviewState,
}: AIResponseRendererProps) => {
  const hasPlanningEnvelope = content.includes('<planning>');
  const isPlanningOpen = hasPlanningEnvelope
    && content.lastIndexOf('<planning>') > content.lastIndexOf('</planning>');
  const renderableContent = useMemo(
    () => content.replace(/<\/?planning>/g, ''),
    [content]
  );
  
  const { processedContent } = useMemo(() => {
    let hasLiveResearch = false;

    const createResourcePreview = (artifact: Resource, isLoading: boolean = false, isGenerating: boolean = false) => {
      const memoizedResource: Resource = {
        ...artifact,
        messageId: artifact.messageId || '',
        timestamp: Date.now().toString(),
        chatId: '',
        type: artifact.type as ResourceType,
      };

      const handleExpand = () => {
        if (handleResourceClick && messageId) {
          handleResourceClick(artifact.title, messageId);
        }
      };

      const matchingVersions = resourcePreviewState?.resources.filter(resource =>
        resource.title === artifact.title && resource.type === artifact.type
      ) || [];
      const viewedResource = resourcePreviewState?.viewedResource;
      const isViewed = Boolean(
        resourcePreviewState?.isResourceViewVisible &&
        viewedResource &&
        viewedResource.title === artifact.title &&
        viewedResource.type === artifact.type
      );

      const latestVersion = matchingVersions.reduce<Resource | null>((latest, resource) => {
        if (!latest || (resource.version || 0) > (latest.version || 0)) return resource;
        return latest;
      }, null);
      const renderedVersion = matchingVersions.find(resource => resource.messageId === messageId);
      // If this message has not reached ResourceContext yet, treat it as the
      // newest candidate to avoid a compact-to-full flash on first render.
      const isOlderVersion = Boolean(
        renderedVersion &&
        latestVersion &&
        (renderedVersion.version || 0) < (latestVersion.version || 0)
      );

      return (
        <ResourcePreview
          key={memoizedResource.id}
          resource={memoizedResource}
          onExpand={handleExpand}
          isLoading={isLoading && isGenerating}
          compact={isOlderVersion || isViewed}
        />
      );
    };

    const processContent = (text: string) => {
      const parts: Array<string | React.ReactElement> = [];
      let currentIndex = 0;
      let resourceCount = 0;
      let imageGenCount = 0;
      let researchActivityCount = 0;
      let composeCount = 0; // Counter for compose / legacy mail tags
      let shoppingCount = 0;
      let leadingPlanningText: string | null = null;

      // Compute think tag ranges to avoid processing <tool_action>/<tool_call> inside them
      const thinkRanges: Array<{ start: number; end: number }> = [];
      const thinkRegex = /<think>([\s\S]*?)(?:<\/think>|$)/g;
      let thinkMatch;
      while ((thinkMatch = thinkRegex.exec(text)) !== null) {
        const start = thinkMatch.index;
        const end = thinkMatch.index + thinkMatch[0].length;
        thinkRanges.push({ start, end });
      }

      // Special UI tags inside Markdown code examples must remain literal.
      const codeRanges: Array<{ start: number; end: number }> = [];
      const codeRegex = /```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$)|`[^`\n]*(?:`|$)/g;
      let codeMatch: RegExpExecArray | null;
      while ((codeMatch = codeRegex.exec(text)) !== null) {
        codeRanges.push({ start: codeMatch.index, end: codeMatch.index + codeMatch[0].length });
      }
      const isInsideCodeRange = (position: number): boolean =>
        codeRanges.some(range => position >= range.start && position < range.end);

      const isLiteralSpecialTag = (position: number, tag: string): boolean => {
        const prevChar = position > 0 ? text[position - 1] : null;
        const nextChar = position + tag.length < text.length ? text[position + tag.length] : null;
        return prevChar === '"' || prevChar === '\'' || prevChar === '`'
          || nextChar === '"' || nextChar === '\'' || nextChar === '`';
      };

      const findOutsideThink = (
        tag: string,
        fromIndex: number,
        options: { allowInsideThink?: boolean; skipCode?: boolean } = {}
      ): number => {
        let pos = text.indexOf(tag, fromIndex);
        while (pos !== -1) {
          const currentPos = pos;
          const insideThink = thinkRanges.some(range =>
            currentPos >= range.start && currentPos < range.end
          );
          const shouldSkipThink = !options.allowInsideThink && insideThink;
          const shouldSkipCode = options.skipCode && isInsideCodeRange(currentPos);
          if (!shouldSkipThink && !shouldSkipCode && !isLiteralSpecialTag(currentPos, tag)) break;
          pos = text.indexOf(tag, currentPos + tag.length);
        }
        return pos;
      };

      // Process resource tags, artifacts, image generation, and tool action tags in order of appearance
      while (currentIndex < text.length) {
        // Find the next occurrence of each tag type
        const resourceStart = text.indexOf('<resource>', currentIndex);
        // Compute next <artifacts> position outside think tags
        const rawArtifactStart = text.indexOf('<artifacts>', currentIndex);
        let artifactIndex = rawArtifactStart;
        while (artifactIndex !== -1) {
          const currentArtifactIndex = artifactIndex;
          const isInsideThink = thinkRanges.some(range =>
            currentArtifactIndex >= range.start && currentArtifactIndex < range.end
          );
          if (!isInsideThink) {
            break;
          }
          artifactIndex = text.indexOf('<artifacts>', artifactIndex + '<artifacts>'.length);
        }
        const imageGenerationStart = text.indexOf('<image_generation>', currentIndex);
        let shoppingStart = findOutsideThink('<shopping>', currentIndex);
        while (
          shoppingStart !== -1 &&
          isInsideCodeRange(shoppingStart)
        ) {
          shoppingStart = findOutsideThink('<shopping>', shoppingStart + '<shopping>'.length);
        }
        const researchTagPositions = [
          findOutsideThink('<think>', currentIndex, { allowInsideThink: true, skipCode: true }),
          findOutsideThink('<reasoning_summary>', currentIndex, { skipCode: true }),
          findOutsideThink('<tool_action>', currentIndex, { skipCode: true }),
          findOutsideThink('<tool_call>', currentIndex, { skipCode: true }),
          // Questions are steps in the activity, not part of the answer.
          findOutsideThink(ASK_USER_OPEN_TAG, currentIndex, { skipCode: true }),
        ].filter(position => position !== -1);
        const researchStart = researchTagPositions.length > 0
          ? Math.min(...researchTagPositions)
          : -1;
        
        // Find compose tag — or legacy <mail> tag — skipping any occurrences inside <think> ranges
        const newComposeStart = findOutsideThink('<compose>', currentIndex);
        const legacyMailStart = findOutsideThink('<mail>', currentIndex);
        const isLegacyMail = legacyMailStart !== -1 &&
          (newComposeStart === -1 || legacyMailStart < newComposeStart);
        const composeStart = isLegacyMail ? legacyMailStart : newComposeStart;
        
        // Determine which tag comes first, or if none exist
        const nextResourceStart = resourceStart !== -1 ? resourceStart : Number.MAX_SAFE_INTEGER;
        const nextArtifactStart = artifactIndex !== -1 ? artifactIndex : Number.MAX_SAFE_INTEGER;
        const nextImageGenerationStart = imageGenerationStart !== -1 ? imageGenerationStart : Number.MAX_SAFE_INTEGER;
        const nextResearchStart = researchStart !== -1 ? researchStart : Number.MAX_SAFE_INTEGER;
        const nextComposeStart = composeStart !== -1 ? composeStart : Number.MAX_SAFE_INTEGER;
        const nextShoppingStart = shoppingStart !== -1 ? shoppingStart : Number.MAX_SAFE_INTEGER;
        const nextTagStart = Math.min(
          nextResourceStart, 
          nextArtifactStart, 
          nextImageGenerationStart, 
          nextResearchStart,
          nextComposeStart,
          nextShoppingStart
        );

        // If no more tags are found, add the remaining text and break
        if (nextTagStart === Number.MAX_SAFE_INTEGER) {
          const remainingText = text.slice(currentIndex).trim(); 
          if (remainingText && isGenerating && isPlanningOpen) {
            // Planning prose still streaming is a live step, in the same pill
            // the research that follows will fill.
            hasLiveResearch = true;
            parts.push(
              <ResearchActivityDisplay
                key={`research-activity-${researchActivityCount++}`}
                items={[{
                  kind: 'narration',
                  content: remainingText,
                  queries: [],
                  urls: [],
                  files: [],
                  isComplete: false,
                }]}
                messageId={messageId || ''}
                isActive
              />
            );
          } else if (remainingText) {
            parts.push(remainingText);
          }
          break;
        }

        // Add text before the next tag if there is any
        if (nextTagStart > currentIndex) {
          const beforeTag = text.slice(currentIndex, nextTagStart).trim();
          if (beforeTag) {
            if (hasPlanningEnvelope && nextTagStart === nextResearchStart) {
              leadingPlanningText = beforeTag;
            } else {
              parts.push(beforeTag);
            }
          }
        }
        // Process the resource tag
        if (nextTagStart === resourceStart) {
          const resourceTag = '<resource>';
          const tagLength = resourceTag.length;
          const prevChar = resourceStart > 0 ? text[resourceStart - 1] : null;
          const nextChar = resourceStart + tagLength < text.length ? text[resourceStart + tagLength] : null;
          const isLiteralTag = prevChar === '"' || prevChar === '\'' || prevChar === '`' || nextChar === '"' || nextChar === '\'' || nextChar === '`';
          
          if (isLiteralTag) {
            currentIndex = resourceStart + tagLength;
            continue;
          }

          const resourceEnd = text.indexOf('</resource>', resourceStart);
          if (resourceEnd === -1) {
            // Partial content during streaming — try XML parse
            const partialContent = text.slice(resourceStart + tagLength);
            const isXml = /(<essay>|<code[\s>]|<visualization[\s>])/.test(partialContent);
            if (isXml) {
              const loadingResource = parseArtifact(partialContent, `loading-${resourceCount}`, resourceCount++, true, messageId || "");
              parts.push(createResourcePreview(loadingResource, true, isGenerating));
            }
            currentIndex = text.length;
            break;
          }

          const resourceContent = text.slice(resourceStart + tagLength, resourceEnd);
          const isXmlContent = /(<essay>|<code[\s>]|<visualization[\s>])/.test(resourceContent);
          if (isXmlContent) {
            // New XML format — same parsing as <artifacts>
            const resource = parseArtifact(resourceContent, `resource-${resourceCount}`, resourceCount++, false, messageId || "");
            parts.push(createResourcePreview(resource));
          } else {
            // Legacy JSON format
            try {
              const resourceData = JSON.parse(resourceContent);
              const resource: Resource = {
                id: `resource-${resourceCount++}`,
                type: resourceData.type || 'essay',
                title: resourceData.title || 'Resource',
                content: resourceContent,
                messageId: messageId || undefined,
                timestamp: Date.now().toString(),
                chatId: '',
              };
              if (resource.type === 'quiz' && resourceData['display-link'] === 'true') {
                parts.push(
                  <QuizItem
                    key={resource.id}
                    resource={resource}
                    messageId={messageId || ""}
                    quizId={typeof resourceData.quizId === 'string' ? resourceData.quizId : undefined}
                  />
                );
              } else {
                parts.push(createResourcePreview(resource));
              }
            } catch (error) {
              console.error("Failed to parse resource tag:", error, "Content:", resourceContent);
            }
          }

          currentIndex = resourceEnd + 11; 
        } 
        // Process the artifact tag
        else if (nextTagStart === artifactIndex) {
          const artifactTag = '<artifacts>';
          const tagLength = artifactTag.length;
          const prevChar = artifactIndex > 0 ? text[artifactIndex - 1] : null;
          const nextChar = artifactIndex + tagLength < text.length ? text[artifactIndex + tagLength] : null;
          const isLiteralTag = prevChar === '"' || prevChar === '\'' || prevChar === '`' || nextChar === '"' || nextChar === '\'' || nextChar === '`';
          
          if (isLiteralTag) {
            currentIndex = artifactIndex + tagLength;
            continue;
          }

          const closingTag = '</artifacts>';
          const closingTagLength = closingTag.length;
          const artifactEnd = text.indexOf(closingTag, artifactIndex + tagLength);

          if (artifactEnd === -1) {
            const partialContent = text.slice(artifactIndex + tagLength);
            const loadingResource = parseArtifact(partialContent, `loading-${resourceCount}`, resourceCount++, true, messageId || "");
            parts.push(createResourcePreview(loadingResource, true, isGenerating));
            currentIndex = text.length; 
            break;
          }

          const artifactContent = text.slice(artifactIndex + tagLength, artifactEnd);
          const resource = parseArtifact(artifactContent, `resource-${resourceCount}`, resourceCount++, false, messageId || "");
          parts.push(createResourcePreview(resource));
          currentIndex = artifactEnd + closingTagLength;
        }
        // Process the image generation tag
        else if (nextTagStart === nextImageGenerationStart) {
          const imageGenTag = '<image_generation>';
          const tagLength = imageGenTag.length;
          const prevChar = imageGenerationStart > 0 ? text[imageGenerationStart - 1] : null;
          const nextChar = imageGenerationStart + tagLength < text.length ? text[imageGenerationStart + tagLength] : null;
          const isLiteralTag = prevChar === '"' || prevChar === '\'' || prevChar === '`' || nextChar === '"' || nextChar === '\'' || nextChar === '`';
          
          if (isLiteralTag) {
            currentIndex = imageGenerationStart + tagLength;
            continue;
          }

          const closingTag = '</image_generation>';
          const closingTagLength = closingTag.length;
          const imageGenEnd = text.indexOf(closingTag, imageGenerationStart + tagLength);

          if (imageGenEnd === -1) {
            currentIndex = text.length; 
            break;
          }

          const imageGenContent = text.slice(imageGenerationStart + tagLength, imageGenEnd);
          try {
            const urlsStr = extractTagContent(imageGenContent, 'urls');
            const isLoadingStr = extractTagContent(imageGenContent, 'is_loading');
            const isErrorStr = extractTagContent(imageGenContent, 'is_error');
            const idStr = extractTagContent(imageGenContent, 'image_generation_id');
            const numberOfImagesStr = extractTagContent(imageGenContent, 'number_of_images');
            const promptStr = extractTagContent(imageGenContent, 'prompt');
            const urls = urlsStr ? JSON.parse(urlsStr) : [];
            const isLoading = isLoadingStr === 'true';
            const isError = isErrorStr === 'true';
            const imageGenerationId = idStr || `img-gen-${imageGenerationStart}-${imageGenCount++}`;
            const numberOfImages = numberOfImagesStr ? parseInt(numberOfImagesStr, 10) : 0;
            const prompt = promptStr ? unescapeXmlText(promptStr) : "";
            const imageToolData: imageToolCall = {
              urls,
              isLoading,
              isError,
              imageGenerationId,
              numberOfImages,
              prompt: prompt
            };

            parts.push(<ImageGeneration key={imageToolData.imageGenerationId} imageGeneration={imageToolData} isMessageGenerating={isGenerating} />);

          } catch (error) {
            console.error("Failed to parse image_generation tag:", error, "Content:", imageGenContent);
          }

          currentIndex = imageGenEnd + closingTagLength;
        }
        // Consecutive thought, reasoning, search, browse, file, and vision tags
        // form one research run. Intermediate prose becomes a timeline step.
        else if (nextTagStart === nextResearchStart) {
          const activityRun = parseResearchActivityRun(text, researchStart, {
            includeTrailingNarration: isPlanningOpen,
            codeRanges,
          });
          if (!activityRun) {
            // A question card still streaming in renders nothing yet.
            if (text.startsWith(ASK_USER_OPEN_TAG, researchStart)) break;
            currentIndex = researchStart + 1;
            continue;
          }

          const activityItems: ResearchActivityItem[] = leadingPlanningText
            ? [{
                kind: 'narration',
                content: leadingPlanningText,
                queries: [],
                urls: [],
                files: [],
                isComplete: true,
              }, ...activityRun.items]
            : activityRun.items;
          leadingPlanningText = null;
          const reachesStreamEnd = text.slice(activityRun.end).trim().length === 0;
          // A question waiting on the user keeps the activity live while paused.
          const isLiveResearch = (isGenerating && reachesStreamEnd) || Boolean(pendingQuestion(activityItems));
          hasLiveResearch = hasLiveResearch || isLiveResearch;
          // Live and finished research render in the same place, so the pill
          // (and an open sheet) carries over when the answer starts.
          parts.push(
            <ResearchActivityDisplay
              key={`research-activity-${researchActivityCount++}`}
              items={activityItems}
              messageId={messageId || ''}
              isActive={isLiveResearch}
            />
          );
          currentIndex = activityRun.end;
          if (!activityRun.isComplete) break;
        }
        // Process shopping results, including the current partially streamed product.
        else if (nextTagStart === nextShoppingStart) {
          const shoppingTag = '<shopping>';
          const closingTag = '</shopping>';
          const shoppingEnd = text.indexOf(closingTag, shoppingStart + shoppingTag.length);
          const isClosed = shoppingEnd !== -1;
          const inner = isClosed
            ? text.slice(shoppingStart + shoppingTag.length, shoppingEnd)
            : text.slice(shoppingStart + shoppingTag.length);
          parts.push(<ShoppingDisplay key={`shopping-${shoppingCount++}`} body={inner} />);
          if (!isClosed) {
            currentIndex = text.length;
            break;
          }
          currentIndex = shoppingEnd + closingTag.length;
        }
        // Process the compose tag (new generic message composition) or legacy <mail> tag
        else if (nextTagStart === nextComposeStart) {
          const composeTag = isLegacyMail ? '<mail>' : '<compose>';
          const tagLength = composeTag.length;
          const prevChar = composeStart > 0 ? text[composeStart - 1] : null;
          const nextChar = composeStart + tagLength < text.length ? text[composeStart + tagLength] : null;
          const isLiteralTag = prevChar === '"' || prevChar === '\'' || prevChar === '`' || nextChar === '"' || nextChar === '\'' || nextChar === '`';

          if (isLiteralTag) {
            currentIndex = composeStart + tagLength;
            continue;
          }

          const closingTag = isLegacyMail ? '</mail>' : '</compose>';
          const closingTagLength = closingTag.length;
          const composeEnd = text.indexOf(closingTag, composeStart + tagLength);
          const isClosed = composeEnd !== -1;
          const inner = isClosed
            ? text.slice(composeStart + tagLength, composeEnd)
            : text.slice(composeStart + tagLength);

          const { to, options } = isLegacyMail
            ? parseLegacyMailBody(inner)
            : parseComposeBody(inner);

          // Legacy <mail> is always an email. For <compose>, the mode is unknown
          // while streaming until either <to> arrives or <options> opens without
          // one (schema puts <to> first) — report "pending" to avoid the header
          // flipping from "Message" to "Email" mid-stream.
          const mode: ComposeBoxMode = isLegacyMail
            ? 'email'
            : !isClosed && !to && !inner.includes('<options')
              ? 'pending'
              : 'auto';

          const key = `compose-${composeStart}-${composeCount++}`;
          parts.push(
            <ComposeBox
              key={key}
              to={to}
              options={options}
              mode={mode}
              isGenerating={!isClosed}
            />
          );

          if (!isClosed) {
            currentIndex = text.length;
            break;
          }
          currentIndex = composeEnd + closingTagLength;
        }
      }

      return parts;
    };

    const parts = processContent(renderableContent);

    // Before the reply produces anything, an empty live pill holds the slot
    // its first activity will fill, so that activity doesn't replace it.
    if (isGenerating && !hasLiveResearch && parts.length === 0) {
      parts.push(
        <ResearchActivityDisplay
          key="research-activity-0"
          items={[]}
          messageId={messageId || ''}
          isActive
        />
      );
    }

    return { processedContent: parts };
  }, [
    renderableContent,
    messageId,
    handleResourceClick,
    isGenerating,
    hasPlanningEnvelope,
    isPlanningOpen,
    resourcePreviewState,
  ]);

  return (
    <div className={`ai-response-content ${isGenerating ? 'generating' : ''}`}>
      {processedContent.map((part, index) => {
        if (!part) return null;
        const uniqueKey = `part-${index}`;
        return typeof part === 'string' ? (
          <AIDisplayRenderer key={uniqueKey} content={part} isGenerating={isGenerating} />
        ) : (
          <React.Fragment key={uniqueKey}>
            {((part as { component?: React.ReactNode })?.component) || part}
          </React.Fragment>
        );
      })}
    </div>
  );
};
